import { Box, Divider, Grid, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useThemeState } from 'contexts/theme/ThemeContext';
import { useMemo } from 'react';
import _ from 'underscore';
import titleCase from 'utils/title-case';
import PlaygroundTabsHeader from '../../../components/playground-tabs-header';
import { CHART_PALETTE } from 'components/charts/constants/chart-palettes';
import { ThemeColor } from '../types/theme-color.type';
import Paper from 'components/paper';

const ColorBox = styled(Box)<{ backgroundColor: string }>(({ backgroundColor }) => ({
  display: 'block',
  alignContent: 'center',
  textAlign: 'center',
  borderRadius: 10,
  fontSize: 20,
  color: 'white',
  backgroundColor: backgroundColor,
  textShadow: '#000 0px 0px 3px',
  height: '4em',
}));

type Props = {};

/**
 *
 * @returns A Palette visualizer component that shows colors used for al charts and the current theme's colors
 */
const Palette: React.FC<Props> = () => {
  const theme = useTheme();
  const { aquaSpecies } = useThemeState();

  const themeColors: { name: string; colors: ThemeColor[] }[] = useMemo(() => {
    let palettes = Object.entries(theme.palette);
    palettes = palettes.slice(2, 22); // should prob not hardcode a range but pluck the colors we want to render
    let colorGroups: { name: string; colors: ThemeColor[] }[] = [];

    palettes.forEach((type) => {
      let types = Object.entries(type[1]);
      let cs: ThemeColor[] = [];

      types.forEach((t) => t[0] !== 'contrastText' && cs.push({ name: `${type[0]} ${t[0]}`, color: t[1] as string }));

      colorGroups.push({ name: type[0], colors: cs });
    });

    return _.sortBy(colorGroups, 'name');
  }, [theme]);

  return (
    <>
      <PlaygroundTabsHeader />
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Box>
          <Typography variant="h2">Palette Visualizer</Typography>
          <Typography variant="body1">
            This component visualizes the various colors used in BluViu charts and the currently chosen theme ({titleCase(aquaSpecies)}
            )
          </Typography>
        </Box>
        <Paper transparent>
          <Typography padding={4} variant="h3" textTransform={'none'} textAlign={'center'}>
            Highcharts Colors
          </Typography>
          <Grid container spacing={2}>
            {CHART_PALETTE.map((color, idx) => (
              <Grid key={`highcharts_${color}_${idx}`} size={{ xs: 6, md: 4 }}>
                <ColorBox key={idx} backgroundColor={color}>
                  {color}
                </ColorBox>
              </Grid>
            ))}
          </Grid>
        </Paper>
        <Paper transparent>
          <Typography variant="h3" padding={4} textTransform={'none'} textAlign={'center'}>
            Theme Colors
          </Typography>
          {themeColors.map(({ name, colors }, themeGroupIdx) => (
            <Box display={'flex'} flexDirection={'column'} gap={4} key={`${name}_${themeGroupIdx}`}>
              <Typography variant="h3" paddingTop={3}>
                {titleCase(name)}
              </Typography>
              <Divider color={theme.palette.common.white} />
              <Grid container spacing={2}>
                {colors.map((value, idx) => (
                  <Grid key={`${value.name}_${idx}`} size={{ xs: 6, md: 4 }} display={'flex'} flexDirection={'row'}>
                    <ColorBox key={idx} width={'100%'} backgroundColor={value.color}>
                      {titleCase(value.name)} - {value.color}
                    </ColorBox>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Paper>
      </Box>
    </>
  );
};

export default Palette;
